import { type CSSProperties, createElement } from "react"

import { accentClasses, badgeIcon } from "@/lib/badge-icons"
import { cn } from "@/lib/utils"

/**
 * OKS-538 — Frame-less badge/blazon display primitive.
 *
 * Renders a brand-uploaded image **as-is** — no tier medallion, no
 * gradient/glow/ring — with subtle rounded corners and `object-cover`.
 * When no image is attached (`imageUrl == null`) it falls back to a
 * plain-color square tinted by the badge's `accentColor` (PRD §3 / Q2 —
 * an interim default with zero committed binary; CTPO swaps real art
 * later). It NEVER renders a broken-image glyph or the old Lucide
 * medallion.
 *
 * OKS-975 — that tinted default may now also carry the badge's own Lucide
 * `icon`, when a caller passes one. This is a GLYPH inside the tint, not
 * the tier medallion: the sentence above still holds. Only
 * `<BadgeMedallion>` opts in, because only the nameless medallion strip has
 * no adjacent text to carry the badge's identity — on the goal board a name
 * sits under every tile, so the flat tint there is correct and unchanged.
 *
 * State affordances (earned checkmark, locked lock, NEW chip) are NOT a
 * frame — the NEW chip is rendered here as an overlay; the checkmark/lock
 * stamps live on the calling `<BadgeTile>`. Locked tiles desaturate.
 *
 * This primitive is intentionally badge-agnostic. OKS-539 (rank blazons)
 * consumes it through the thin `<RankBlazon>` wrapper: badges pass an
 * `accentColor` token (→ a pastel Tailwind class), while ranks pass a
 * `defaultStyle` (the shield tier hex-gradient from `blazonFor`) since
 * ranks have no accent token. When `defaultStyle` is supplied it OVERRIDES
 * the accent-tint class for the no-image default; badge call sites are
 * unchanged (they don't pass it).
 *
 * # Accessibility (UX Spec §6)
 *
 * - With an image: `alt` = the localized badge name (unless `decorative`,
 *   when the caller — e.g. `<BadgeTile>` — already labels the wrapper, so
 *   the image is `alt=""` to avoid a double announce).
 * - The accent-tinted default is always decorative (`aria-hidden`), glyph
 *   included: the icon is a visual hint, never the accessible name. Callers
 *   still name the badge in adjacent text or, on the nameless medallion
 *   strip, in the wrapper's own `aria-label`. A sighted user gains a cue; a
 *   screen-reader user's output does not change at all.
 * - State (earned/locked/new) is never conveyed by the image alone — the
 *   stamps + text label carry it.
 */
export type BadgeImageSize =
  | "hero"
  | "preview"
  | "modal"
  | "inline"
  | "corner"
  | "detail"

const SIZE_CLASSES: Record<BadgeImageSize, string> = {
  hero: "h-[60px] w-[60px]",
  preview: "h-12 w-12",
  modal: "h-24 w-24",
  // OKS-539 rank-surface additions — `inline` welcome-header (40px),
  // `corner` avatar pin (28px, matches RankPatch `sm`).
  inline: "h-10 w-10",
  corner: "h-7 w-7",
  // OKS-527 — §14 detail-sheet medallion (124px).
  detail: "h-[124px] w-[124px]",
}

/**
 * OKS-975 — glyph dimensions for the accent-tinted default's `icon` (BRIEF
 * §5.1). A `Record` over the WHOLE size union rather than a lookup with a
 * fallback: a size added to `BadgeImageSize` later then fails `tsc` here
 * instead of silently resolving to `undefined` and rendering an unsized SVG.
 *
 * The four sizes `<PremiumBadge>` also defines carry ITS values verbatim
 * (`hero` 28, `preview` 24, `corner` 14, `detail` 56) so a milestone and a
 * custom badge read as siblings at the same size — which is the entire point
 * of D1. `modal` and `inline` have no tier-medallion counterpart and are
 * proportional; neither is reachable this ticket (only the medallion passes
 * `icon`, at `preview` and `corner`).
 */
const ICON_CLASSES: Record<BadgeImageSize, string> = {
  hero: "h-7 w-7",
  preview: "h-6 w-6",
  modal: "h-12 w-12",
  inline: "h-5 w-5",
  corner: "h-3.5 w-3.5",
  detail: "h-14 w-14",
}

interface BadgeImageProps {
  /** Brand-uploaded image URL. `null`/absent ⇒ tinted default. */
  imageUrl?: string | null
  /**
   * Accent token driving the default tint
   * (`BadgeCatalog::ACCENT_COLORS`). Optional — ranks pass `defaultStyle`
   * instead (they have no accent token).
   */
  accentColor?: string
  /**
   * OKS-539 — Inline style for the no-image default (e.g. the rank tier
   * hex-gradient). When present it OVERRIDES the `accentColor` Tailwind
   * class. Badges omit it.
   */
  defaultStyle?: CSSProperties
  /**
   * OKS-975 — Lucide identifier drawn INSIDE the accent-tinted default
   * (`BadgeCatalog::CUSTOM_ICONS`, resolved via `badgeIcon` with its `Award`
   * fallback). Optional, and opted into by ONE call site: `<BadgeMedallion>`.
   *
   * It applies to the accent-tint arm ONLY. The `defaultStyle` arm — rank
   * blazons — is unreachable from here by arm ORDER, not by a check: a rank
   * has no icon concept and must never grow a glyph even if a future caller
   * passes one. Omitting it reproduces today's flat tint exactly, which is
   * the contract `<BadgeTile>` and the creator goal board depend on.
   */
  icon?: string
  /** Localized badge name — used as the image `alt`. */
  name: string
  /** `hero` 60 (grid) | `preview` 48 (preview row) | `modal` 96 (earn). */
  size?: BadgeImageSize
  /** Desaturates the tile for the locked variant. */
  state?: "earned" | "locked"
  /** Pins the celebratory NEW chip (earned + recent). */
  isNew?: boolean
  /** Already-translated NEW chip label. */
  newLabel?: string
  /**
   * When true the wrapper already labels the badge (e.g. `<BadgeTile>`),
   * so the `<img>` is `alt=""` to avoid announcing the name twice.
   */
  decorative?: boolean
  className?: string
}

export function BadgeImage({
  imageUrl,
  accentColor,
  defaultStyle,
  icon,
  name,
  size = "hero",
  state = "earned",
  isNew = false,
  newLabel = "New",
  decorative = false,
  className,
}: BadgeImageProps) {
  const locked = state === "locked"

  return (
    <div
      data-testid="badge-image"
      data-has-image={imageUrl ? "true" : "false"}
      data-state={state}
      data-size={size}
      className={cn("relative shrink-0", className)}
    >
      <div
        className={cn(
          "overflow-hidden rounded-lg",
          SIZE_CLASSES[size],
          locked && "opacity-70 grayscale",
        )}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={decorative ? "" : name}
            data-testid="badge-image-img"
            className="h-full w-full object-cover"
          />
        ) : defaultStyle ? (
          <div
            aria-hidden="true"
            data-testid="badge-image-default"
            className="h-full w-full"
            style={defaultStyle}
          />
        ) : (
          <div
            aria-hidden="true"
            data-testid="badge-image-default"
            className={cn(
              "flex h-full w-full items-center justify-center",
              accentClasses(accentColor ?? "purple").bg,
            )}
          >
            {/*
              OKS-975 — the glyph sits INSIDE this wrapper, which is already
              `aria-hidden`, so the accessibility tree is byte-identical to
              before: the default stays decorative and the surrounding text
              label (or the medallion's `aria-label`) remains the only name.
              Absent `icon`, this renders nothing and the arm is the flat tint
              it has always been.
            */}
            {/*
              No `data-testid` on the glyph: `LucideProps` does not admit one
              and forcing it would need a cast. The assertion hook is the `svg`
              INSIDE `badge-image-default` — which is the honest thing to query
              anyway, since "this arm renders a glyph" and "this arm renders no
              glyph" are both statements about the presence of an svg here.
              `<PremiumBadge>` renders its glyph the same way.
            */}
            {icon &&
              createElement(badgeIcon(icon), {
                className: cn(
                  ICON_CLASSES[size],
                  accentClasses(accentColor ?? "purple").fg,
                ),
                "aria-hidden": "true",
              })}
          </div>
        )}
      </div>

      {isNew && (
        <span
          data-testid="badge-image-new-chip"
          className="absolute -top-1 -left-1 rounded-full bg-[linear-gradient(135deg,#fbbf24_0%,#f59e0b_100%)] px-1.5 py-0.5 text-[9px] leading-none font-bold text-amber-950 shadow-[0_0_8px_rgba(251,191,36,0.85)]"
        >
          {newLabel}
        </span>
      )}
    </div>
  )
}

export default BadgeImage
